/* eslint-disable */
import React, {
  useEffect, useState, useMemo, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { useChat } from 'chat/useChat';
import { Skeleton } from 'components/Skeleton';
import app from 'new-ui/app';
import { useTranslation } from 'utils/modifiedTranslation';
import { NBA_CHAT_ROOM } from 'chat/constants';
import { POPUP } from 'utils/constants/popup';
import { INTERVENTIONAL_TYPES_OPTIONS } from 'modules/matchesFilter/constants';
import { GA_NBA_CATEGORY, GA_NBA_EVENTS } from 'modules/ga/generalEvents';
import { useMatchesFilter } from 'modules/matchesFilter/MatchesFilterContext';
import { getUserWixId } from 'modules/user/utils';
import StepJourneyCard from './StepJourenyCard/StepJourenyCard';
import styles from './Nba.module.css';
import { startNbaSession, updateNbaState } from './api';
import TreatmentTypeSelector from './CustomActionComponents/TreatmentTypeSelector';
import CategoryTypeSelector from './CustomActionComponents/CategoryTypeSelector';
import { evaluateActionConditions } from './nbaUtils';

const ACTION_IDENTIFIER_RENDER_COMPONENT = 'renderComponent';

const componentMap = {
  TreatmentTypeSelector,
  CategoryTypeSelector,
};

const getTreatmentTypeSelectorProps = ({ commonArgs, setTreatmentTypes }) => ({
  ...commonArgs,
  onSelectionChange: (selectedValues) => {
    setTreatmentTypes(selectedValues);
    console.log('TreatmentTypeSelector selection changed:', selectedValues);
  },
  initialSelection: commonArgs.initialSelection || 'All',
});

const getCategoryTypeSelectorProps = ({
  commonArgs, setDynamicActionComponentCallback, setInterventionalTypes, openChatPopup
}) => {
  const currentParams = new URLSearchParams(window.location.search);
  const initialInterventionalTypes = currentParams.getAll('interventional_types');
  return {
    ...commonArgs,
    categories: INTERVENTIONAL_TYPES_OPTIONS,
    initialSelection: commonArgs.initialSelection || initialInterventionalTypes,
    onApply: (selectedValues) => {
      setInterventionalTypes(selectedValues);
      console.log('CategoryTypeSelector applied:', selectedValues);
    },
    onLearnMore: () => {
      const message = commonArgs.learnMoreMessage || 'Tell me more about treatment categories.';
      if (typeof setDynamicActionComponentCallback === 'function') {
        setDynamicActionComponentCallback(null);
      }
      openChatPopup({
        popupType: POPUP.ASSISTANCE_CHAT,
        popupData: { preventOverlayClose: true },
        data: { initialUserMessage: message },
      });
    },
  };
};

const createFrontendActionHandlers = ({
  setDynamicActionComponentRef,
  setInterventionalTypes,
  setTreatmentTypes,
  openChatPopup,
}) => ({
  openTinaAndSendMessage: (args, context) => {
    setDynamicActionComponentRef(null);
    const messageToSend = args?.message || `Tell me more about ${context.actionName || 'this action'}.`;
    const userSystemPrompt = args?.userSystemPrompt || null;

    openChatPopup({ data: { initialUserMessage: messageToSend, userSystemPrompt } });
  },
  TalkWithUs: (args, context) => {
    setDynamicActionComponentRef(null);
    app.setPopup(POPUP.CALLUS, { theme: 'middle', preventOverlayClose: true });
  },
  OpenNGS: (args, context) => {
    setDynamicActionComponentRef(null);
    app.setPopup(POPUP.UPLOAD, { theme: 'middle', preventOverlayClose: true });
  },
  shareWithDoc: (args, context) => {
    setDynamicActionComponentRef(null);
    app.setPopup(POPUP.SHARE_WITH_MY_DOCTOR);
  },
  updateUserState: (args, context) => { setDynamicActionComponentRef(null); console.log('Action: updateUserState', args, context); },
  answerQuestionsAboutTrialAndTreatment: (args, context) => { setDynamicActionComponentRef(null); console.log('Action: answerQuestionsAboutTrialAndTreatment', args, context); },
  updateStep: (args, context) => { setDynamicActionComponentRef(null); console.log('Action: updateStep', args, context); },
  determineAndApplyGuideUpdate: (args, context) => { setDynamicActionComponentRef(null); console.log('Action: determineAndApplyGuideUpdate', args, context); },
  renderComponent: (args, context) => {
    const componentName = args?.componentName;
    const ComponentToRender = componentMap[componentName];
    if (ComponentToRender) {
      const baseProps = { actionData: context.action, context, ...(args?.componentProps || {}) };
      let finalProps = baseProps;
      if (componentName === 'TreatmentTypeSelector') {
        finalProps = getTreatmentTypeSelectorProps({
          commonArgs: baseProps, setTreatmentTypes,
        });
      } else if (componentName === 'CategoryTypeSelector') {
        finalProps = getCategoryTypeSelectorProps({
          commonArgs: baseProps, setDynamicActionComponentCallback: setDynamicActionComponentRef, setInterventionalTypes,
        });
      }
      setDynamicActionComponentRef({ Component: ComponentToRender, props: finalProps });
    } else {
      console.warn(`Frontend: Component "${componentName}" not found in componentMap for click handler.`);
      setDynamicActionComponentRef(null);
    }
  },
  defaultAction: (identifier, actionName, context) => {
    console.warn(`Frontend: Default action handler triggered for identifier "${identifier}" (Action: ${actionName}). Opening Tina.`);
    setDynamicActionComponentRef(null);
    const initialUserMessage = `Tell me more about the action: ${actionName}.`;
    openChatPopup({
      popupType: POPUP.ASSISTANCE_CHAT,
      popupData: { preventOverlayClose: true },
      data: { initialUserMessage },
    });
  },
});

const Nba = () => {
  const { t } = useTranslation();

  const { data: profile } = useSelector((state) => state.profile);
  const { trials } = useSelector((state) => state.matches);

  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [activeMilestoneId, setActiveMilestoneId] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [currentActionName, setCurrentActionName] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [dynamicActionComponent, setDynamicActionComponent] = useState(null);
  const { treatmentTypes: selectedTreatmentTypes, setInterventionalTypes, setTreatmentTypes } = useMatchesFilter();
  const { openChatPopup } = useChat(NBA_CHAT_ROOM);

  const userWixId = getUserWixId(profile);

  const evaluationContext = useMemo(() => {
    const missingInfo = profile.missing_critical_info || {};
    const isStandardOfCareSelected = Array.isArray(selectedTreatmentTypes)
                                     && selectedTreatmentTypes.length === 1
                                     && selectedTreatmentTypes[0] === 'standard_of_care';
    const biomarkers = profile.condition_profile?.biomarkers;
    const have_biomarker = Array.isArray(biomarkers) && biomarkers.length > 0;
    return {
      userHaveSponseredTrialList: trials.some((trial) => trial?.isSponsored),
      has_phase_3_trials: trials.some((trial) => trial?._phases?.includes(3)),
      has_missing_critical_info: Object.values(missingInfo).some((isMissing) => isMissing === true),
      has_targeted_trials: trials.some((trial) => trial?.groups?.includes('Targeted chemotherapy')),
      has_location: !!profile.info?.country,
      trial_within_100_miles: trials.some((trial) => typeof trial.nearest_facility_distance === 'number' && trial.nearest_facility_distance < 100),
      selected_standard_only: isStandardOfCareSelected,
      have_biomarker: have_biomarker,
    };
  }, [profile, trials]);

  const frontendActionHandlers = useMemo(
    () => createFrontendActionHandlers({
      setDynamicActionComponentRef: setDynamicActionComponent,
      setInterventionalTypes,
      setTreatmentTypes,
      openChatPopup,
    }),
    [setInterventionalTypes, setTreatmentTypes, openChatPopup],
  );

  const persistNbaState = useCallback(async (newState) => {
    if (!userWixId) {
      console.warn('Cannot persist NBA state: userWixId not found.');
      return;
    }
    const payload = {
      userWixId,
      currentMilestone: newState.milestoneId,
      currentStep: newState.stepId,
      currentAction: newState.actionName,
    };
    try {
      await updateNbaState(payload);
    } catch (error) {
      console.error('Failed to persist NBA state:', error);
    }
  }, [userWixId]);

  const { milestonesForCard, completedMilestoneIds } = useMemo(() => {
    const nbaGuide = sessionData?.nbaGuide;
    if (!nbaGuide || !activeMilestoneId) {
      return { milestonesForCard: [], completedMilestoneIds: [] };
    }
    const milestones = nbaGuide.milestones ?? [];
    if (!Array.isArray(milestones)) {
      return { milestonesForCard: [], completedMilestoneIds: [] };
    }
    const currentMilestoneIndex = milestones.findIndex((m) => m?.name === activeMilestoneId);
    if (currentMilestoneIndex === -1) {
      return { milestonesForCard: milestones, completedMilestoneIds: [] };
    }
    const completedIds = currentMilestoneIndex > 0
      ? milestones.slice(0, currentMilestoneIndex).map((m) => m?.name).filter(Boolean)
      : [];
    return { milestonesForCard: milestones, completedMilestoneIds: completedIds };
  }, [sessionData, activeMilestoneId]);

  const currentAction = useMemo(() => {
    if (!sessionData || !activeMilestoneId || !selectedStepId || !currentActionName) return null;
    const currentMilestoneData = sessionData.nbaGuide?.milestones?.find((m) => m.name === activeMilestoneId);
    const currentStepData = currentMilestoneData?.steps?.find((s) => s.name === selectedStepId);

    // Get all actions for the current step, ordered
    const allActionsInStep = (currentStepData?.actions ?? [])
      .map((action, index) => ({
        ...action,
        order: typeof action.order === 'number' ? action.order : index + 1,
      }))
      .sort((a, b) => a.order - b.order);

    // Filter these actions based on conditions using the evaluationContext
    const visibleActions = allActionsInStep.filter((action) => evaluateActionConditions(action.conditions, evaluationContext));
    return visibleActions.find((a) => a.name === currentActionName) || null;
  }, [sessionData, activeMilestoneId, selectedStepId, currentActionName, evaluationContext]);

  useEffect(() => {
    if (currentAction?.renderAnotherComponent) {
      const componentName = currentAction.renderAnotherComponent;
      const ComponentToRender = componentMap[componentName];
      if (ComponentToRender) {
        const contextForProps = {
          userWixId,
          sessionData,
          activeMilestoneId,
          selectedStepId,
          actionName: currentAction.name,
          action: currentAction,
        };
        const componentSpecificArgs = currentAction.renderAnotherComponentArgs || {};
        const generalActionArgs = currentAction.args || {};

        const baseProps = {
          actionData: currentAction,
          context: contextForProps,
          ...generalActionArgs,
          ...componentSpecificArgs,
        };

        let finalProps = baseProps;
        if (componentName === 'TreatmentTypeSelector') {
          finalProps = getTreatmentTypeSelectorProps({ commonArgs: baseProps, setTreatmentTypes });
        } else if (componentName === 'CategoryTypeSelector') {
          finalProps = getCategoryTypeSelectorProps({
            commonArgs: baseProps, setDynamicActionComponentCallback: setDynamicActionComponent, setInterventionalTypes,
          });
        }
        setDynamicActionComponent({ Component: ComponentToRender, props: finalProps, originatingActionName: currentAction.name });
      } else {
        console.warn(`NBA: Component "${componentName}" for action "${currentAction.name}" not found in componentMap.`);
        setDynamicActionComponent(null);
      }
    } else {
      setDynamicActionComponent(null);
    }
  }, [userWixId, currentAction, sessionData, activeMilestoneId, selectedStepId]);

  const handlePagedActionChange = useCallback((newActionName) => {
    if (newActionName !== currentActionName) {
      // console.log(`NBA: Paged action changed to: ${newActionName}. Current step: ${selectedStepId}`);
      setCurrentActionName(newActionName);
      persistNbaState({
        milestoneId: activeMilestoneId,
        stepId: selectedStepId,
        actionName: newActionName,
      });
    }
  }, [currentActionName, activeMilestoneId, selectedStepId, persistNbaState]);

  const handleMilestoneClick = useCallback((milestoneName) => {
    if (milestoneName === activeMilestoneId || !sessionData?.nbaGuide?.milestones) return;

    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.MILESTONE_TAB_CLICKED,
      { milestone: milestoneName },
    );

    const newMilestone = sessionData.nbaGuide.milestones.find((m) => m?.name === milestoneName);
    if (!newMilestone) return;
    const firstStep = newMilestone.steps?.[0];
    const firstStepName = firstStep?.name;

    setActiveMilestoneId(milestoneName);
    setSelectedStepId(firstStepName || null);
    setCurrentActionName(null); // Reset, StepJourneyCard will inform via onPagedActionChange

    if (firstStepName) { // Persist milestone and step, actionName will be updated by onPagedActionChange
      persistNbaState({ milestoneId: milestoneName, stepId: firstStepName, actionName: null });
    }
  }, [activeMilestoneId, sessionData, persistNbaState]);

  const handleStepClick = useCallback((stepName) => {
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.STEP_TAB_CLICKED,
      { milestone: activeMilestoneId, step: stepName },
    );
    if (stepName === selectedStepId || !sessionData?.nbaGuide?.milestones || !activeMilestoneId) return;

    setSelectedStepId(stepName);
    setCurrentActionName(null); // Reset, StepJourneyCard will inform via onPagedActionChange

    persistNbaState({ milestoneId: activeMilestoneId, stepId: stepName, actionName: null });
  }, [selectedStepId, activeMilestoneId, sessionData, persistNbaState]);

  const handleActionClick = useCallback(async (actionToExecute) => {
    if (!actionToExecute || typeof actionToExecute?.name !== 'string') {
      console.error('Invalid action clicked:', actionToExecute);
      return;
    }

    if (actionToExecute.name !== currentActionName) {
      setCurrentActionName(actionToExecute.name);
      persistNbaState({
        milestoneId: activeMilestoneId,
        stepId: selectedStepId,
        actionName: actionToExecute.name,
      });
    }

    const identifier = actionToExecute.onActionCallIdentifier;
    if (identifier && identifier !== ACTION_IDENTIFIER_RENDER_COMPONENT) {
      const handlerKey = typeof identifier === 'string' ? identifier : String(identifier);
      const handler = frontendActionHandlers[handlerKey] || frontendActionHandlers.defaultAction;
      const context = {
        userWixId,
        sessionData,
        activeMilestoneId,
        selectedStepId,
        actionName: actionToExecute.name,
        action: actionToExecute,
      };
      const args = actionToExecute.args || {};
      try {
        setIsActionLoading(true);
        if (typeof handler === 'function') {
          if (handler === frontendActionHandlers.defaultAction) {
            handler(handlerKey, actionToExecute.name, context);
          } else {
            await handler(args, context);
          }
        } else {
          frontendActionHandlers.defaultAction(handlerKey, actionToExecute.name, context);
        }
      } catch (frontendError) {
        console.error(`Error executing frontend handler for action "${actionToExecute.name}" (identifier: ${handlerKey}):`, frontendError);
      } finally {
        setIsActionLoading(false);
      }
    } else if (!identifier) {
      // console.log(`Action "${actionToExecute.name}" has no specific onActionCallIdentifier or renderAnotherComponent.`);
    }
  }, [sessionData, activeMilestoneId, selectedStepId, currentActionName, frontendActionHandlers, persistNbaState]);

  useEffect(() => {
    const initializeNbaSession = async () => {
      setIsLoadingSession(true);
      setSessionError(null);
      let userWixId = app.user?.info?.user_wix_id; // Use let to allow re-assignment
      let indication = app.user?.personal?.condition; // Use let

      if (!userWixId || !indication) {
        // Short delay to allow app.user to potentially populate if not immediately available
        await new Promise((resolve) => setTimeout(resolve, 300));
        userWixId = app.user?.info?.user_wix_id; // Re-check
        indication = app.user?.personal?.condition; // Re-check

        if (!userWixId || !indication) {
          const errorMessage = 'User Wix ID or Indication is missing after waiting. Cannot start NBA session.';
          console.error(errorMessage, { userWixId, indication }); // Log current values
          setSessionError(errorMessage);
          setIsLoadingSession(false);
          return;
        }
      }

      const dataToSend = { userWixId, cancerType: indication };
      try {
        const response = await startNbaSession(dataToSend);
        if (response && response.nbaGuide && response.currentMilestone && response.currentStep) {
          setSessionData(response);
          setActiveMilestoneId(response.currentMilestone);
          setSelectedStepId(response.currentStep);
          setCurrentActionName(response.currentAction || null);

          if (response.message && !response.success) { // Assuming backend might send success flag
            console.warn('NBA Session Warning:', response.message);
          }
        } else {
          setSessionError(response?.error || response?.message || 'Failed to start NBA session (API response missing expected data).');
        }
      } catch (err) {
        setSessionError(err.message || 'An unexpected error occurred during session start.');
      } finally {
        setIsLoadingSession(false);
      }
    };
    initializeNbaSession();
  }, []); // Empty dependency array, runs once on mount

  if (isLoadingSession) {
    return (
      <div className={styles.main_box}>
        <Skeleton width="100%" className={styles.skeleton} />
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className={styles.main_box} style={{ color: 'red', padding: '20px', border: '1px solid red' }}>
        {t('nba.session_error_message', 'Error loading guidance. Please try again later.')}
        {process.env.NODE_ENV === 'development' && (
          <pre>
            Details:
            {sessionError}
          </pre>
        )}
      </div>
    );
  }

  const canRenderCards = !!(sessionData?.nbaGuide && activeMilestoneId && selectedStepId);

  return (
    <div className={styles.main_box}>
      {canRenderCards ? (
        <StepJourneyCard
          milestones={milestonesForCard}
          activeMilestoneId={activeMilestoneId}
          selectedStepId={selectedStepId}
          completedMilestoneIds={completedMilestoneIds}
          onMilestoneClick={handleMilestoneClick}
          onStepClick={handleStepClick}
          onActionClick={handleActionClick}
          isActionLoading={isActionLoading}
          dynamicActionComponent={dynamicActionComponent}
          onPagedActionChange={handlePagedActionChange}
          evaluationContext={evaluationContext}
        />
      ) : (
        !isLoadingSession && <div>{t('nba.guidance_unavailable', 'Guidance data is currently unavailable.')}</div>
      )}
    </div>
  );
};

export default Nba;
